package link.erpango.quip.domain.noteimport

import link.erpango.quip.QuipConstants
import link.erpango.quip.domain.entities.DMaintenance
import link.erpango.quip.domain.entities.DMySparePart
import link.erpango.quip.domain.entities.DSparePart
import link.erpango.quip.domain.entities.DSparePartCatalog
import link.erpango.quip.domain.entities.DVehicle
import link.erpango.quip.domain.entities.VehicleType

/**
 * Builds the entities the existing create calls take from reviewed drafts. Each returns null
 * while a required field is missing (see DraftValidation.kt), so an incomplete draft can
 * never be sent. Values are built exactly like the corresponding creation forms do.
 */

fun VehicleDraft.toDVehicle(): DVehicle? {
    if (missingRequired().isNotEmpty()) return null
    return DVehicle(
        vin = vin!!.value,
        plate = plate!!.value,
        brand = brand!!.value,
        model = model!!.value,
        vehicleType = vehicleType?.value ?: VehicleType.CAR,
        year = year!!.value,
        color = color!!.value,
        owner = owner!!.value,
        engineOil = engineOil?.value.orEmpty(),
        frontTireSize = frontTireSize?.value.orEmpty(),
        rearTireSize = rearTireSize?.value.orEmpty(),
        recommendedFuel = recommendedFuel?.value.orEmpty(),
        engineDetails = engineDetails?.value.orEmpty(),
        cylinderCount = cylinderCount?.value ?: 0,
        frontBulbType = frontBulbType?.value.orEmpty(),
        rearBulbType = rearBulbType?.value.orEmpty(),
        batterySpecs = batterySpecs?.value.orEmpty(),
        coolantType = coolantType?.value.orEmpty(),
        frontTirePressureBelow160 = frontTirePressureBelow160?.value ?: 0.0,
        rearTirePressureBelow160 = rearTirePressureBelow160?.value ?: 0.0,
        frontTirePressureAbove160 = frontTirePressureAbove160?.value ?: 0.0,
        rearTirePressureAbove160 = rearTirePressureAbove160?.value ?: 0.0,
        nextItvDate = nextItvDate?.value,
        firstRegistrationDate = firstRegistrationDate?.value,
    )
}

/** Unsigned: the caller chain-signs it like MaintenancesViewModel.addMaintenance does. */
fun MaintenanceDraft.toDMaintenance(vehicleVin: String, spareParts: List<DSparePart> = emptyList()): DMaintenance? {
    if ((missingRequired() - MaintenanceField.VEHICLE).isNotEmpty()) return null
    return DMaintenance(
        id = null,
        vehicleVin = vehicleVin,
        kilometers = kilometers!!.value,
        description = description?.value?.takeIf { it.isNotBlank() } ?: "N/A",
        spareParts = spareParts,
        mechanic = mechanic?.value?.takeIf { it.isNotBlank() } ?: "N/A",
        workshop = workshop?.value?.takeIf { it.isNotBlank() } ?: "N/A",
        cost = cost!!.value,
        date = date!!.value,
    )
}

/** A personal spare part ("Mis repuestos") linked to [vehicleVins]. */
fun SparePartDraft.toDMySparePart(vehicleVins: List<String>): DMySparePart? {
    if (missingRequired().isNotEmpty()) return null
    return DMySparePart(
        categoryId = categoryId!!.value,
        sparePartId = typeId!!.value,
        brand = brand!!.value,
        model = model!!.value,
        reference = reference?.value,
        vehicleVins = vehicleVins,
    )
}

/**
 * A spare part used in a maintenance. Types with a quantity (oil, brake fluid) also need one,
 * as in AddSparePartScreen.
 */
fun SparePartDraft.toDSparePart(catalog: DSparePartCatalog): DSparePart? {
    if (missingRequired().isNotEmpty()) return null
    val type = catalog.sparePartTypes[typeId!!.value] ?: return null
    if (type.hasQuantity && quantity == null) return null
    return DSparePart(
        categoryId = categoryId!!.value,
        sparePartId = type.id,
        brand = brand!!.value,
        model = model!!.value,
        additionalInfo = "",
        durabilityDays = 0,
        quantity = if (type.hasQuantity) quantity!!.value else 1.0,
        unit = type.unit ?: QuipConstants.Units.count,
        reference = reference?.value,
    )
}
